import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import type { Client } from '../../types/crm';

type VisitationAddAccountsModalProps = {
  open: boolean;
  onClose: () => void;
  clients: Client[];
  adding: boolean;
  onAdd: (clientIds: string[]) => Promise<void>;
  onAddProspect: (companyName: string) => Promise<void>;
};

const LIST_LIMIT = 200;

export function VisitationAddAccountsModal({
  open,
  onClose,
  clients,
  adding,
  onAdd,
  onAddProspect,
}: VisitationAddAccountsModalProps) {
  const [tab, setTab] = useState<'accounts' | 'prospect'>('accounts');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [prospectName, setProspectName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab('accounts');
    setQuery('');
    setSelected(new Set());
    setProspectName('');
    setError(null);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...clients].sort((a, b) => a.company_name.localeCompare(b.company_name));
    if (!q) return sorted.slice(0, LIST_LIMIT);
    return sorted
      .filter((c) => {
        const pool = `${c.company_name} ${c.code} ${c.client_type ?? ''}`.toLowerCase();
        return pool.includes(q);
      })
      .slice(0, LIST_LIMIT);
  }, [clients, query]);

  const selectedInView = filtered.filter((c) => selected.has(String(c.id))).length;
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(String(c.id)));

  function toggleOne(clientId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const c of filtered) next.delete(String(c.id));
      } else {
        for (const c of filtered) next.add(String(c.id));
      }
      return next;
    });
  }

  async function handleSubmitAccounts(e: React.FormEvent) {
    e.preventDefault();
    const ids = [...selected];
    if (ids.length === 0) {
      setError('Select at least one account.');
      return;
    }
    setError(null);
    try {
      await onAdd(ids);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add accounts.');
    }
  }

  async function handleSubmitProspect(e: React.FormEvent) {
    e.preventDefault();
    const name = prospectName.trim();
    if (!name) {
      setError('Enter the company / customer name.');
      return;
    }
    setError(null);
    try {
      await onAddProspect(name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add new customer.');
    }
  }

  return (
    <Modal title="Add to visitation list" open={open} onClose={onClose} wide>
      <div className="dashboard-visitation-add">
        <div className="dashboard-visitation-add__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'accounts'}
            className={`dashboard-visitation-add__tab${tab === 'accounts' ? ' is-active' : ''}`}
            onClick={() => {
              setTab('accounts');
              setError(null);
            }}
          >
            Existing accounts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'prospect'}
            className={`dashboard-visitation-add__tab${tab === 'prospect' ? ' is-active' : ''}`}
            onClick={() => {
              setTab('prospect');
              setError(null);
            }}
          >
            New customer
          </button>
        </div>

        {error ? <div className="alert alert-error">{error}</div> : null}

        {tab === 'accounts' ? (
          <form
            className="dashboard-visitation-add__form"
            onSubmit={(e) => void handleSubmitAccounts(e)}
          >
            <div className="field">
              <span className="field-label">Search</span>
              <div className="dashboard-visitation-add__search-row">
                <input
                  className="field-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by name or code"
                  autoFocus
                />
                <button
                  type="button"
                  className="btn-secondary btn-sm dashboard-visitation-add__select-filtered"
                  onClick={toggleAllFiltered}
                >
                  {allFilteredSelected ? 'Clear filtered' : 'Select filtered'}
                </button>
              </div>
              <p className="dashboard-visitation-add__meta">
                {selected.size} selected
                {selectedInView > 0 && selectedInView !== selected.size
                  ? ` · ${selectedInView} in view`
                  : ''}
                {clients.length > LIST_LIMIT && !query.trim()
                  ? ` · showing first ${LIST_LIMIT} — search to find more`
                  : ''}
              </p>
            </div>

            <ul className="dashboard-visitation-add__list" role="listbox" aria-multiselectable>
              {filtered.length === 0 ? (
                <li className="dashboard-visitation-add__empty">No matching accounts.</li>
              ) : (
                filtered.map((client) => {
                  const id = String(client.id);
                  const checked = selected.has(id);
                  return (
                    <li key={id}>
                      <label className="dashboard-visitation-add__row">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(id)}
                        />
                        <span className="dashboard-visitation-add__name">{client.company_name}</span>
                        <span className="dashboard-visitation-add__code">{client.code}</span>
                      </label>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="dashboard-visitation-add__actions">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={adding}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={adding || selected.size === 0}>
                {adding
                  ? 'Adding…'
                  : `Add ${selected.size || ''} account${selected.size === 1 ? '' : 's'}`.trim()}
              </button>
            </div>
          </form>
        ) : (
          <form
            className="dashboard-visitation-add__form"
            onSubmit={(e) => void handleSubmitProspect(e)}
          >
            <p className="dashboard-visitation-add__hint">
              For customers not yet created in CRM. You may still add a visit task, then mark the visit as completed.
            </p>
            <label className="field">
              <span className="field-label">Company / customer name</span>
              <input
                className="field-input"
                value={prospectName}
                onChange={(e) => setProspectName(e.target.value)}
                placeholder="e.g. New Cafe Pte Ltd"
                autoFocus
              />
            </label>
            <div className="dashboard-visitation-add__actions">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={adding}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={adding || !prospectName.trim()}
              >
                {adding ? 'Adding…' : 'Add new customer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
